<script setup lang="ts">
import type { AcMilestone } from '~/types/crm';
import { formatDate } from '@/mixins/visualFormatters';
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import AccaFileDisplayer from '@/components/reusable/accaFileDisplayer.vue'

const emits = defineEmits(["deleteMilestone"])

const store = useStore()
const userProfile = computed(() => store.getters['user/userProfile'])
const hoveredMilestone = ref<number | null>(null)

const emitDeleteMilestone = (milestone: AcMilestone) => {
    emits("deleteMilestone", milestone)
}



const props = defineProps<{
    milestones: AcMilestone[],
    iconSize: string,
}>()

</script>

<template>
    <div class="milestone-displayer">
        <div 
            v-for="(milestone, index) in props.milestones.slice().reverse()" 
            :key="milestone.id || milestone.date.toString()" 
            class="milestone-section"
        >
            <div class="milestone-item border border-gray-500 rounded-xl p-6" @mouseenter="hoveredMilestone = index" @mouseleave="hoveredMilestone = null">
                <div class="flex items-start gap-3 mb-8 justify-between">
                    <div class="title-container">
                        <div class="icon-container">
                            <span class="material-icons icon text-[var(--text-color)]">{{ milestone.icon }}</span>
                        </div>
                    <h3 class="text-4xl mb-0 text-[var(--text-color)]">{{ milestone.title }}</h3>
                    </div>
                    <div v-if="milestone.user.id === userProfile?.uid && hoveredMilestone === index" class="user-container text-2xl">
                        <div @click="emitDeleteMilestone(milestone)" class="ac-minimal-button">
                            <p class="text-red-400 cursor-pointer :hover:text-red-500">Sil</p>
                        </div>
                    </div>
                    <div v-else class="user-container text-2xl">
                        <p>{{ milestone.user.userName }}</p>
                        <p>{{ formatDate(milestone.date.toDate()) }}</p>
                    </div>
                </div>
                <p v-if="milestone.explanation" class="text-2xl mb-3">{{ milestone.explanation }}</p>
                
                <!-- Documents section -->
                <div v-if="milestone.documents && milestone.documents.length > 0" class="documents-section mt-6">
                    <h4 class="text-2xl font-semibold mb-4 text-[var(--text-color)]">Yüklenen Dosyalar</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div v-for="document in milestone.documents" :key="document.url" class="document-item p-3 border border-gray-300 rounded-lg bg-gray-50">
                            <acca-file-displayer :file="document" />
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Connector - only show if not the last element -->
            <div v-if="index < props.milestones.slice().reverse().length - 1" class="milestone-connector">
                <div class="connector-arrow"></div>
                <div class="connector-line"></div>
            </div>
        </div>
    </div>
</template>

<style scoped>

.icon-container{
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5rem;
    height: 5rem;
    border-radius: 50%;
    background: white;
    padding: 1rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 2.4rem;
}

.icon {
    font-size: v-bind(iconSize);
}

.milestone-section {
    margin-bottom: 1rem;
}

.milestone-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 3rem 0;
    position: relative;
}

.connector-line {
    width: 2px;
    height: 6rem;
    background: transparent;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
}

.connector-line::before,
.connector-line::after {
    content: '• • • • • •';
    color: var(--accent-gray-subtle);
    font-size: 8px;
    line-height: 0.8;
    letter-spacing: 0;
    writing-mode: vertical-lr;
    opacity: 0.7;
    position: absolute;
    top: 0.5rem;
    left: 50%;
    transform: translateX(-50%);
}

.connector-line::after {
    content: '';
    top: auto;
    bottom: -4px;
    width: 8px;
    height: 8px;
    background: var(--accent-gray);
    border-radius: 50%;
    box-shadow: 0 0 8px rgba(180, 180, 180, 0.4);
    font-size: unset;
    line-height: unset;
    letter-spacing: unset;
    writing-mode: unset;
}

.connector-arrow {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 8px solid var(--accent-gray);
    margin-bottom: 0.5rem;
    filter: drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.2));
    position: relative;
}


</style>